import { observer } from "mobx-react";
import { Form, Input, Modal, Select, Tag } from "antd";
import { translateKey as t } from "../../../../../../locales/translation";
import { ClientsController } from "../../../ClientsController";
import { AppStore } from "~/stores/AppStore";
import { useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";

const CloneClientModal = observer(({ store, controller }: { store?: AppStore; controller?: ClientsController }) => {
  const [form] = Form.useForm();
  const [clientRefStatus, setClientRefStatus] = useState<string>(null);
  const latestResolveRef = useRef<(value: unknown) => void>();
  const latestRejectRef = useRef<(reason?: any) => void>();

  const { cloneClientModal, partners } = controller;
  const source = cloneClientModal.source;

  // PRO-84: partners served by another OpenFleet stack are shown but cannot be selected as clone target.
  const partnerNotInEnvironmentText = t("lblPartnerNotInCurrentEnvironment", "Served by another OpenFleet environment — log in as that partner to create its clients");
  const noPartnersForEnvironment = partners.length > 0 && partners.every((p) => !p.isCurrentEnvironment);

  useEffect(() => {
    if (cloneClientModal.visible) {
      controller.loadPartners();
      form.resetFields();
      setClientRefStatus(null);
    }
  }, [cloneClientModal.visible, controller, form]);

  const checkClientRef = async (clientRef: string): Promise<boolean> => {
    return store.getData(`clients/validate?ref=${clientRef}`, "pro_client", false);
  };

  const debouncedCheck = useMemo(
    () =>
      debounce(async (value: string) => {
        try {
          const exists = await checkClientRef(value);
          setClientRefStatus(exists ? "error" : "success");

          if (!exists) {
            latestResolveRef.current?.(true);
          } else {
            latestRejectRef.current?.(new Error(t("lblClientRefEnteredInUse", "The Client ref entered is already in use")));
          }
        } catch (err) {
          setClientRefStatus("error");
          latestRejectRef.current?.(new Error("Validation failed. Please try again."));
        }
      }, 1500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const validateClientRef = (_: any, value: string) => {
    setClientRefStatus("");

    if (!value) {
      setClientRefStatus("error");
      return Promise.reject(new Error(t("lblClientRefIsRequired", "Client Ref is required")));
    }
    if (value.length < 3) {
      setClientRefStatus("error");
      return Promise.reject(new Error("Client Ref must be at least 3 characters long"));
    }
    if (value.length > 10) {
      setClientRefStatus("error");
      return Promise.reject(new Error("Client Ref must be at most 10 characters long"));
    }

    return new Promise((resolve, reject) => {
      latestResolveRef.current = resolve;
      latestRejectRef.current = reject;
      setClientRefStatus("validating");
      debouncedCheck(value);
    });
  };

  const handleCancel = () => {
    form.resetFields();
    controller.setCloneClientModal(null, { visible: false, source: null });
  };

  const handleOK = () => {
    form
      .validateFields()
      .then((values) => {
        const partner = partners.find((p) => p._id === values.partnerId);
        if (!partner) {
          store.displayNotification(t("lblCloneTargetPartner", "Partner") + ": " + t("lblRequired", "Required"), "error", true);
          return;
        }

        // PRO-84: refuse to clone into a partner served by another OpenFleet stack (the API rejects it as well).
        if (!partner.isCurrentEnvironment) {
          store.displayNotification(
            t("lblPartnerEnvironmentMismatch", "Partner {1} is served by another OpenFleet environment; log in as that partner to create its clients", [partner.clientRef || partner.ref]),
            "error",
            true
          );
          return;
        }

        const payload = {
          source: { _id: source._id, clientRef: source.clientRef, dbName: source.dbName },
          target: { clientRef: values.clientRef, name: values.name },
          partner: { _id: partner._id, ref: partner.clientRef || partner.ref },
        };

        controller.cloneClient(payload);
      })
      .catch(() => {});
  };

  return (
    <Modal
      title={t("lblCloneClient", "Clone Client")}
      open={cloneClientModal.visible}
      cancelText={t("lblCancel", "Cancel")}
      okText={t("lblClone", "Clone")}
      onCancel={handleCancel}
      onOk={handleOK}
      centered
      okType="primary"
    >
      {source && (
        <div className="flex justify-center items-center">
          <Form form={form} className="w-full max-w-md" layout="vertical">
            <div className="mb-4 text-sm text-gray-600">
              {t("lblCloningFrom", "Cloning from")}: <strong>{source.name}</strong> ({source.clientRef})
            </div>

            <Form.Item
              className="w-full block mb-4"
              label={t("lblCloneTargetPartner", "Partner")}
              name="partnerId"
              rules={[{ required: true, message: t("lblPartnerIsRequired", "Partner is required") }]}
            >
              <Select
                showSearch
                style={{ width: "100%", textAlign: "left" }}
                placeholder={t("lblSelectAPartner", "Select a partner")}
                optionFilterProp="children"
              >
                {partners.map((p) => (
                  <Select.Option key={p._id} value={p._id} disabled={!p.isCurrentEnvironment} title={!p.isCurrentEnvironment ? partnerNotInEnvironmentText : undefined}>
                    <div className="flex justify-between">
                      <div>{p.name}</div>
                      <div>
                        <Tag color={p.isCurrentEnvironment ? "green" : "default"} className="mr-1" title={p.isCurrentEnvironment ? t("lblCurrentEnvironment", "Current environment") : partnerNotInEnvironmentText}>
                          {p.environmentHost || t("lblUnknownEnvironment", "Unknown environment")}
                        </Tag>
                        <Tag color="blue" className="mr-0">
                          {p.clientRef || p.ref}
                        </Tag>
                      </div>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            {noPartnersForEnvironment && (
              <div className="mb-4 text-xs text-gray-500 text-left">{t("lblNoPartnersForThisEnvironment", "None of the partners are served by the OpenFleet environment you are logged in to")}</div>
            )}

            <Form.Item
              className="w-full block mb-4"
              label={t("lblTargetClientRef", "Target Client Ref")}
              name="clientRef"
              rules={[
                { required: true, message: t("lblClientRefIsRequired", "Client Ref is required") },
                { validator: validateClientRef },
              ]}
              validateStatus={clientRefStatus as any}
              hasFeedback
            >
              <Input
                placeholder={t("lblEnterClientRef", "Enter client reference")}
                onInput={(e: any) => {
                  e.target.value = e?.target?.value.toUpperCase();
                }}
                onKeyDown={(e: any) => {
                  if (e.key.includes(" ")) {
                    e.preventDefault();
                  }
                }}
              />
            </Form.Item>

            <Form.Item
              className="w-full block mb-4"
              label={t("lblTargetClientName", "Target Client Name")}
              name="name"
              rules={[{ required: true, whitespace: true, message: t("lblClientNameIsRequired", "Client Name is required") }]}
            >
              <Input placeholder={t("lblEnterClientName", "Enter client name")} />
            </Form.Item>
          </Form>
        </div>
      )}
    </Modal>
  );
});

export default CloneClientModal;
